import { type ReactNode } from 'react';
import {
  Button,
  FieldError,
  Label,
  ListBox,
  Popover,
  Select,
  type SelectProps,
  SelectValue,
  Text,
} from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import styles from './Picker.module.css';

export type PickerProps<T extends object> = Omit<SelectProps<T>, 'children'> & {
  label?: string;
  description?: ReactNode;
  errorMessage?: string;
  items?: Iterable<T>;
  selectedLabel?: string;
  iconName?: IconName;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  listFooter?: ReactNode;
  className?: string;
};

export function Picker<T extends object>({
  items,
  label,
  description,
  errorMessage,
  children,
  selectedLabel,
  iconName = 'chevrons-out',
  listFooter,
  className,
  ...props
}: PickerProps<T>) {
  return (
    <Select aria-label={label || selectedLabel} {...props} className={className}>
      {label && <Label>{label}</Label>}
      <Button className={styles.button}>
        {selectedLabel && <div className={styles.selectedItemPrefix}>{selectedLabel}</div>}
        <SelectValue />
        <Icon name={iconName} size="small" slot="end-icon" />
      </Button>
      {description && <Text slot="description">{description}</Text>}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
      <Popover>
        <ListBox items={items}>{children}</ListBox>
        {listFooter && <div className={styles.listFooter}>{listFooter}</div>}
      </Popover>
    </Select>
  );
}
